import React, {useEffect, useRef} from "react";
import {
    ColumnsDirective,
    FilterSettingsModel,
    GridComponent,
    PageSettingsModel,
    TextWrapSettingsModel,
    ColumnDirective,
    TextAlign,
    Inject,
    Resize,
    Search,
    Sort,
    Reorder,
    Toolbar,
    Edit,
    Filter,
    Page,
    ExcelExport,
    DetailRow, GridModel, Column, RowSelectEventArgs, RowDataBoundEventArgs,
} from "@syncfusion/ej2-react-grids";
import {ClickEventArgs} from "@syncfusion/ej2-navigations";
import {GridSkeleton} from "../Skeletons/GridSkeleton";
import {Button} from "@mui/material";

export interface GridColumns {
    field: string,
    headerText: string,
    width: string,
    textAlign: string,
}

export interface ReportFilter {
    column: string,
    operator: string,
    filter: string,
}

export interface TemplateRow {
    field: string,
    headerText: string,
    templateFunction: (args: any) => JSX.Element,
}

interface GenericReportGridProps<T> {
    columns: GridColumns[],
    data: T[],
    pageSize: number,
    dataExportFileName: string,
    isLoading?: boolean,
    filter?: ReportFilter,
    childColumns?: Column[],
    childDataSource?: any[],
    queryString?: string,
    templateRows?: TemplateRow[],
}
function GenericReportGrid<T>({columns, pageSize, data, dataExportFileName, isLoading, filter, childColumns, childDataSource, queryString, templateRows} : GenericReportGridProps<T>) {
    const toolbarOptions = ['Search', 'ExcelExport'];
    const grid = useRef<GridComponent>(null);
    const filterOptions: FilterSettingsModel = {type: 'Excel'};
    const textWrapSettings: TextWrapSettingsModel = {wrapMode: "Header"}
    const pageSettings: PageSettingsModel = { pageSize: pageSize };
    const childGridOptions : GridModel | undefined = childColumns ? {
        columns: childColumns,
        dataSource: childDataSource,
        queryString: queryString,
    } : undefined;
    
    useEffect(() => {
        if(filter && grid.current) {
            grid.current.filterByColumn(filter.column, filter.operator, filter.filter);
        } else {
            grid.current?.clearFiltering();
        }
    }, [filter]);
    
    const created = () => {
        document?.getElementById(grid.current?.element.id + "_searchbar")?.addEventListener("keyup", (event) => {
            grid.current?.search((event.target as HTMLInputElement).value)
        })
    }
    
    const dataBound = () => {
        if(grid) {
            grid.current?.autoFitColumns(columns.map(column => column.field));
        }
    }

    const onToolbarClick = async (args: ClickEventArgs) => {
        if (grid.current && args.item.id === grid.current.element.id + '_excelexport') {
            await grid.current.excelExport({fileName: dataExportFileName});
        }
    }

    if(isLoading) {
        return (
            <div style={{height: '100%'}}>
                <GridSkeleton columns={columns.length} rows={20}/>
            </div>
        );
    }
    
    return (
        <GridComponent style={{padding: '1em'}} id={'generic-report-grid'}
                       childGrid={childGridOptions}
                       width="100%" height="100%"
                       ref={grid}
                       enableStickyHeader
                       toolbar={toolbarOptions}
                       toolbarClick={onToolbarClick}
                       created={created}
                       dataSource={data}
                       allowResizing
                       allowPaging
                       allowSorting
                       allowFiltering
                       allowExcelExport
                       statelessTemplates={['directiveTemplates']}
                       resizeSettings={{mode: 'Auto'}}
                       pageSettings={pageSettings}
                       allowTextWrap
                       textWrapSettings={textWrapSettings}
                       filterSettings={filterOptions} dataBound={dataBound} allowReordering
                       loadingIndicator={{indicatorType: 'Shimmer'}}
        >
            <ColumnsDirective>
                {columns.map((column, index) => (
                    <ColumnDirective
                        key={index}
                        field={column.field}
                        width={column.width}
                        textAlign={column.textAlign as TextAlign}
                        headerText={column.headerText}
                    />
                ))}
                {
                    templateRows &&
                    templateRows.map((templateRow, index) => (
                        <ColumnDirective key={index} field={templateRow.field} headerText={templateRow.headerText} template={templateRow.templateFunction} />
                    ))
                }
            </ColumnsDirective>
            <Inject services={[Toolbar, Resize, Search, Sort, Reorder, ExcelExport, Edit, Filter, Page, DetailRow]}/>
        </GridComponent>
    )
}

export default GenericReportGrid;